import { useMemo, useState } from 'react';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import IconCancel from '@mui/icons-material/Cancel';
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import {
  Button,
  useListContext,
  useNotify,
  usePermissions,
  useRefresh,
  useUnselectAll,
} from 'react-admin';
import { DEFAULT_API_ADMIN, PERMISSIONS } from '../../constants';
import { httpClientReactAdmin } from '../../lib';

type CouponRow = {
  id: string;
  redeemed?: boolean;
  refunded?: boolean;
};

type BulkValidateResult = {
  validated: number;
  alreadyRedeemed: number;
  refunded: number;
  notFound: number;
  expired: number;
};

const buildSummary = ({ validated, alreadyRedeemed, refunded, notFound, expired }: BulkValidateResult) => {
  const parts = [`${validated} cupom(ns) validado(s)`];
  if (expired) { parts.push(`${expired} estava(m) vencido(s)`); }
  if (alreadyRedeemed) { parts.push(`${alreadyRedeemed} já resgatado(s)`); }
  if (refunded) { parts.push(`${refunded} estornado(s)`); }
  if (notFound) { parts.push(`${notFound} não encontrado(s)`); }
  return parts.join(' · ');
};

/**
 * Ação em lote "Validar" do Relatório de Cupons — só admin.
 *
 * Marca os cupons selecionados como resgatados (`redeemed`, `redeemedAt`,
 * `redemptionPlaceId`, `redemptionEmployeeId`) sem passar pelo resgate de caixa:
 * não gera código nem chama integração externa (RTI/EVO/Trevo). É correção manual
 * de troca feita fora do app. Cupom já resgatado ou estornado é ignorado pelo backend.
 */
const ValidateCouponsBulkAction = ({ data = [] as CouponRow[] }) => {
  const { selectedIds, resource } = useListContext();
  const { permissions } = usePermissions();
  const notify = useNotify();
  const refresh = useRefresh();
  const unselectAll = useUnselectAll(resource);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // O backend é a autoridade sobre o que entra; isto é só o aviso na tela de confirmação,
  // e só enxerga as linhas já carregadas (seleção de outra página cai no "backend decide").
  const skippable = useMemo(() => {
    const selected = data.filter(({ id }) => selectedIds.includes(id));
    return selected.filter(coupon => coupon.redeemed || coupon.refunded).length;
  }, [data, selectedIds]);

  if (!permissions || !permissions.includes(PERMISSIONS.ADMIN)) { return null; }

  const submit = async () => {
    setLoading(true);
    try {
      const { json } = await httpClientReactAdmin(
        `${DEFAULT_API_ADMIN}/coupons/bulk-validate`,
        { method: 'POST', body: JSON.stringify({ couponIds: selectedIds }) },
      );
      const result: BulkValidateResult = json?.data?.[0] || json;
      notify(buildSummary(result), { type: result?.validated ? 'success' : 'warning' });
      setOpen(false);
      unselectAll();
      refresh();
    } catch (error: any) {
      notify(error?.body?.message || error?.message || 'Erro ao validar cupons', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        label="Validar"
        startIcon={<FactCheckIcon />}
        disabled={!selectedIds.length}
        onClick={() => setOpen(true)}
      />
      <Dialog open={open} onClose={() => !loading && setOpen(false)}>
        <DialogTitle>Validar cupons</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`Marcar ${selectedIds.length} cupom(ns) selecionado(s) como resgatado(s) agora?`}
          </DialogContentText>
          <DialogContentText sx={{ marginTop: 1 }}>
            O local de troca fica igual ao local de origem do cupom. Nenhum código é gerado e
            nenhuma integração externa é acionada.
          </DialogContentText>
          {skippable > 0 &&
            <DialogContentText sx={{ marginTop: 1, fontWeight: 'bold' }}>
              {`${skippable} cupom(ns) da seleção já está(ão) resgatado(s) ou estornado(s) e será(ão) ignorado(s).`}
            </DialogContentText>
          }
        </DialogContent>
        <DialogActions>
          <Button startIcon={<IconCancel />} disabled={loading} label="ra.action.cancel" onClick={() => setOpen(false)} />
          <Button
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <FactCheckIcon />}
            label="ra.action.confirm"
            onClick={submit}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ValidateCouponsBulkAction;
